/**
 * Runs at most `concurrency` tasks at once; the rest wait in FIFO order, except
 * urgent ones, which jump the line. Car mode puts every TTS request through it:
 * ElevenLabs refuses requests beyond the plan's concurrency limit with 429.
 */
export class TaskQueue {
    private running = 0;
    private readonly waiting: (() => void)[] = [];

    constructor(private readonly concurrency: number) {}

    run<T>(task: () => Promise<T>, urgent = false): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const start = () => {
                this.running++;
                task()
                    .then(resolve, reject)
                    .finally(() => {
                        this.running--;
                        this.waiting.shift()?.();
                    });
            };

            if (this.running < this.concurrency)
                start();
            else if (urgent)
                this.waiting.unshift(start);
            else
                this.waiting.push(start);
        });
    }
}

/** Retries a failing task after growing pauses (`delaysMs`), then gives up with the last error. */
export async function withRetry<T>(task: () => Promise<T>, delaysMs: number[]): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await task();
        } catch (error) {
            if (attempt >= delaysMs.length)
                throw error;
            await new Promise(resolve => setTimeout(resolve, delaysMs[attempt]));
        }
    }
}
